import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AOS from 'aos';
import 'aos/dist/aos.css';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import FloatingButtons from './components/FloatingButtons';

// Pages
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import Portfolio from './pages/Portfolio';
import Pricing from './pages/Pricing';
import Contact from './pages/Contact';

// Service Pages
import WebDevelopment from './pages/services/WebDevelopment';
import MobileApps from './pages/services/MobileApps';
import DigitalMarketing from './pages/services/DigitalMarketing';
import TravelPortals from './pages/services/TravelPortals';
import POSSystems from './pages/services/POSSystems';
import BookingSystems from './pages/services/BookingSystems';
import RentalSystems from './pages/services/RentalSystems';
import RealEstate from './pages/services/RealEstate';
import CompanyManagement from './pages/services/CompanyManagement';
import PattayaSolutions from './pages/services/PattayaSolutions';

// Blog & Admin Pages
import AdminLogin from './pages/admin/Login';
import AdminDashboard from './pages/admin/Dashboard';
import BlogEditor from './pages/admin/BlogEditor';
import Categories from './pages/admin/Categories';
import Enquiries from './pages/admin/Enquiries';
import EnquiryDebugger from './pages/admin/EnquiryDebugger';
import BlogList from './pages/blog/BlogList';
import BlogPost from './pages/blog/BlogPost';
import ProtectedRoute from './components/admin/ProtectedRoute';
import AdminLayout from './components/admin/AdminLayout';

// Client Management Pages
import ClientsList from './pages/admin/clients/ClientsList';
import ClientForm from './pages/admin/clients/ClientForm';
import ClientDetail from './pages/admin/clients/ClientDetail';
import ClientsStats from './pages/admin/clients/ClientsStats';
// Project Management Pages
import ProjectsList from './pages/admin/projects/ProjectsList';
import ProjectForm from './pages/admin/projects/ProjectForm';
import ProjectsProgress from './pages/admin/projects/ProjectsProgress';
// Payment Management Pages
import PaymentsList from './pages/admin/payments/PaymentsList';
import PaymentForm from './pages/admin/payments/PaymentForm';
import PaymentsReports from './pages/admin/payments/PaymentsReports';
import PaymentsPending from './pages/admin/payments/PaymentsPending';
import Settings from './pages/admin/Settings';
import PaymentInvoice from './pages/admin/payments/PaymentInvoice';

// Contexts
import { AuthProvider } from './contexts/AuthContext';
import { BlogProvider } from './contexts/BlogContext';
import { EnquiryProvider } from './contexts/EnquiryContext';

// i18n
import './i18n';

function AppContent() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      offset: 100
    });
  }, []);

  return (
    <AuthProvider>
      <BlogProvider>
        <EnquiryProvider>
          <div className="App">
            {!isAdmin && <Navbar />}
            <main>
              <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/services/web-development" element={<WebDevelopment />} />
                  <Route path="/services/mobile-apps" element={<MobileApps />} />
                  <Route path="/services/digital-marketing" element={<DigitalMarketing />} />
                  <Route path="/services/travel-portals" element={<TravelPortals />} />
                  <Route path="/services/pos-systems" element={<POSSystems />} />
                  <Route path="/services/booking-systems" element={<BookingSystems />} />
                  <Route path="/services/rental-systems" element={<RentalSystems />} />
                  <Route path="/services/real-estate" element={<RealEstate />} />
                  <Route path="/services/company-management" element={<CompanyManagement />} />
                  <Route path="/services/pattaya-solutions" element={<PattayaSolutions />} />
                  <Route path="/portfolio" element={<Portfolio />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/contact" element={<Contact />} />
                  
                  {/* Blog Routes */}
                  <Route path="/blog" element={<BlogList />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  
                  {/* Admin Routes */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  
                  {/* Admin Panel with Sidebar Layout */}
                  <Route path="/admin" element={
                    <ProtectedRoute>
                      <AdminLayout />
                    </ProtectedRoute>
                  }>
                    <Route path="dashboard" element={<AdminDashboard />} />
                    <Route path="posts" element={<AdminDashboard />} />
                    <Route path="posts/new" element={<BlogEditor />} />
                    <Route path="posts/edit/:id" element={<BlogEditor />} />
                    <Route path="categories" element={<Categories />} />
                    <Route path="contacts" element={<Enquiries />} />
                    <Route path="enquiry-debugger" element={<EnquiryDebugger />} />
                    
                    {/* Client Management Routes */}
                    <Route path="clients" element={<ClientsList />} />
                    <Route path="clients/new" element={<ClientForm />} />
                    <Route path="clients/:id" element={<ClientDetail />} />
                    <Route path="clients/edit/:id" element={<ClientForm />} />
                    <Route path="clients/stats" element={<ClientsStats />} />
                    
                    {/* Project Management Routes */}
                    <Route path="projects" element={<ProjectsList />} />
                    <Route path="projects/new" element={<ProjectForm />} />
                    <Route path="projects/edit/:id" element={<ProjectForm />} />
                    <Route path="projects/progress" element={<ProjectsProgress />} />
                    
                    {/* Payment Management Routes */}
                    <Route path="payments" element={<PaymentsList />} />
                    <Route path="payments/new" element={<PaymentForm />} />
                    <Route path="payments/edit/:id" element={<PaymentForm />} />
                    <Route path="payments/reports" element={<PaymentsReports />} />
                    <Route path="payments/pending" element={<PaymentsPending />} />
                    <Route path="payments/invoice/:id" element={<PaymentInvoice />} />
                    
                    {/* Settings */}
                    <Route path="settings" element={<Settings />} />
                  </Route>
              </Routes>
            </main>
            {!isAdmin && <Footer />}
            {!isAdmin && <FloatingButtons />}
          </div>
        </EnquiryProvider>
      </BlogProvider>
    </AuthProvider>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
